import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Tracks whether an element has entered the viewport, latching to true on the
 * first intersection (we never need to unload once seen). `rootMargin` lets
 * callers prefetch slightly before an element scrolls into view.
 *
 * Degrades to "in view" immediately where IntersectionObserver is unavailable.
 */
export function useInView<T extends Element>(
  rootMargin = "200px",
): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(
    typeof IntersectionObserver === "undefined",
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || inView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setInView(true);
      },
      { rootMargin },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView];
}
